import { useCallback, useContext, useEffect, useState } from 'react';
import { createContext } from 'react';

import { IListItemValues } from '../../components/dropdown/dropdown-filtered-list';
import { DEFAULT_HEADER } from '../../constants/reports/reports-data';
import {
  ButtonType,
  RISK_METRIC_LIST,
  TIME_LIST,
  TOKENS,
  VOL_SHOCK_LIST,
  excludeDisabledRiskMetrics,
} from '../../constants/reports/reports-filters';
import { DecayWidgetContext } from '../decay-widget/decay-widget-provider';
import { PortfolioManagementContext } from '../portfolio-management';
import { WebSocketContext } from '../websocket-context/websocket-provider';

import { RiskFiltersContext } from './risk-filters-provider';
import {
  CounterAccountsAggregatedResponseProps,
  IGetDataParams,
  IRiskManagementParamsProvider,
  IRiskManagementProvider,
} from './types';
import { useListRiskSnapshots } from './use-list-risk-snapshots';
import { useLoadRiskReport } from './use-load-risk-report';
import usePersistentState from '../use-persistent-state';

export enum riskTypes {
  spotLadder = 'SPOT_LADDER',
  spotGrid = 'SPOT_GRID',
  spotTime = 'SPOT_TIME',
}

export enum riskTokenTypes {
  quote = 'quote',
  base = 'base',
}

export const RiskManagementContext = createContext<IRiskManagementProvider>(
  {} as IRiskManagementProvider,
);

const FIXED_RISK_TYPES = ['PV', 'PNL', 'DELTA', 'GAMMA', 'THETA', 'VEGA'] as const;

const DYNAMIC_RISK_TYPES = [
  'DELTA(STICKY_DELTA)',
  'INTRINSIC_DELTA',
  'VOLGA',
  'VANNA',
  'DVEGADSPOT',
  'DDELTADVOL',
  'SMILE_25',
  'SKEW_25',
] as const;

const START_KEY = 'RISK_GET_DATA_';

export const RiskManagementProvider = ({ children }: IRiskManagementParamsProvider) => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const { addSpotDataToAccount, addAggregatedData, riskSettingsSelected, freezeSpotMapRef } =
    useContext(PortfolioManagementContext);
  const {
    filters: { setVolShock, setTime, riskMetric, setRiskMetric },
  } = useContext(RiskFiltersContext);

  const { getRisks } = useContext(DecayWidgetContext);

  const [data, setData] = useState<any[]>([]);
  const [dataGraph, setDataGraph] = useState<any[]>([]);
  const [loadingByRequestId, setLoadingByRequestId] = useState<Record<string, boolean>>({});
  const [isAutoUpdate, setIsAutoUpdate] = usePersistentState(
    ['RISK_FILTERS'],
    'autoUpdateSpotLadder',
    false,
  );
  const [counterAccountsAggregatedResponse, setCounterAccountsAggregatedResponse] =
    useState<CounterAccountsAggregatedResponseProps>({
      SPOT_LADDER_QUOTE: 0,
      SPOT_LADDER_BASE: 0,
      SPOT_GRID_QUOTE: 0,
      SPOT_GRID_BASE: 0,
      SPOT_TIME_QUOTE: 0,
      SPOT_TIME_BASE: 0,
    });
  const [isSpotLadderQuoteLoading, setIsSpotLadderQuoteLoading] = useState(false);
  const [isSpotLadderBaseLoading, setIsSpotLadderBaseLoading] = useState(false);
  const [isSpotGridQuoteLoading, setIsSpotGridQuoteLoading] = useState(false);
  const [isSpotGridBaseLoading, setIsSpotGridBaseLoading] = useState(false);
  const [isSpotTimeQuoteLoading, setIsSpotTimeQuoteLoading] = useState(false);
  const [isSpotTimeBaseLoading, setIsSpotTimeBaseLoading] = useState(false);

  const [selectedTimestampPerAccount, setSelectedTimestampPerAccount] = useState<
    Record<string, IListItemValues>
  >({});
  const { getListRiskSnapshots, listRiskSnapshotsLoading, riskSnapshotsData } =
    useListRiskSnapshots();
  const { getLoadRiskReport } = useLoadRiskReport({
    setLoading: setIsSpotLadderQuoteLoading,
  });

  const isRiskAllSpotLoading =
    isSpotLadderQuoteLoading ||
    isSpotLadderBaseLoading ||
    isSpotGridQuoteLoading ||
    isSpotGridBaseLoading ||
    isSpotTimeQuoteLoading ||
    isSpotTimeBaseLoading;

  const KEY = `${START_KEY}`;

  const spotData = data?.find((item: any) => item.length && item[0]?.id === 'spot')?.[0];

  const getStringToUpperCase = (str: string) => str?.toUpperCase();

  const getRequestId = useCallback(
    ({
      exchange = '',
      riskToken = '',
      matrix = '',
      account = '',
      isAggregated = false,
    }: {
      exchange?: string;
      riskToken?: string;
      matrix?: string;
      account?: string;
      isAggregated?: boolean;
    }) => {
      const accountKey = isAggregated || !account ? 'AGG' : `ACC_${getStringToUpperCase(account)}`;

      return `${KEY}${matrix}_${getStringToUpperCase(exchange)}_${getStringToUpperCase(riskToken)}_${accountKey}`;
    },
    [],
  );

  const setLoadingForRequestId = useCallback((id: string, isLoading: boolean) => {
    setLoadingByRequestId((prev) => ({ ...prev, [id]: isLoading }));
  }, []);

  const isLoadingForRequestId = useCallback(
    (id: string) => !!loadingByRequestId[id],
    [loadingByRequestId],
  );

  const isLoadingByParams = useCallback(
    (params: {
      exchange?: string;
      riskToken?: string;
      matrix?: string;
      account?: string;
      isAggregated?: boolean;
    }) => !!loadingByRequestId[getRequestId(params)],
    [getRequestId, loadingByRequestId],
  );

  const handleResponse = useCallback(
    (
      response: any,
      {
        accounts,
        exchange,
        dataParent,
        riskToken,
        riskMetrics,
        isSpotOverride,
        isFreezeSpotOverride,
        freezeSpotValue,
        isOtcTraderVols,
        isSpotAggregatedAccounts,
        autoUpdate,
      }: any,
    ) => {
      if (!response) return;

      const { id, result, error } = response;
      const isExchangeResponse = id.includes(getStringToUpperCase(exchange));
      const isRiskTokenResponse = id.includes(getStringToUpperCase(riskToken));

      const responseAccountKey = Array.isArray(accounts)
        ? 'AGG'
        : `ACC_${getStringToUpperCase(accounts?.name || '')}`;
      const idSuffix = id.split('_').pop();
      const keySuffix = responseAccountKey.split('_').pop();
      const isAccountResponse = idSuffix === keySuffix;

      if (
        !id.startsWith(START_KEY) ||
        !isExchangeResponse ||
        !isRiskTokenResponse ||
        !isAccountResponse
      )
        return;

      const updateCounterAndLoading = (
        loadingSetter: any,
        counterKey: keyof CounterAccountsAggregatedResponseProps,
      ) => {
        if (isSpotAggregatedAccounts) {
          setCounterAccountsAggregatedResponse((state) => ({
            ...state,
            [counterKey]: state[counterKey] + 1,
          }));
        } else {
          loadingSetter(false);
        }
      };

      const setLoadingByRiskToken = () => {
        switch (riskToken) {
          case 'quote':
            switch (dataParent) {
              case 'SPOT_LADDER':
                updateCounterAndLoading(setIsSpotLadderQuoteLoading, 'SPOT_LADDER_QUOTE');
                break;
              case 'SPOT_GRID':
                updateCounterAndLoading(setIsSpotGridQuoteLoading, 'SPOT_GRID_QUOTE');
                break;
              case 'SPOT_TIME':
                updateCounterAndLoading(setIsSpotTimeQuoteLoading, 'SPOT_TIME_QUOTE');
                break;
              default:
                break;
            }
            break;
          case 'base':
            switch (dataParent) {
              case 'SPOT_LADDER':
                updateCounterAndLoading(setIsSpotLadderBaseLoading, 'SPOT_LADDER_BASE');
                break;
              case 'SPOT_GRID':
                updateCounterAndLoading(setIsSpotGridBaseLoading, 'SPOT_GRID_BASE');
                break;
              case 'SPOT_TIME':
                updateCounterAndLoading(setIsSpotTimeBaseLoading, 'SPOT_TIME_BASE');
                break;
              default:
                break;
            }
            break;
          default:
            break;
        }
      };

      const data = result ? (isSpotAggregatedAccounts ? result : response) : [];
      const errorFlag = !result && error;

      const commonProps = {
        dataParent,
        data,
        error: errorFlag,
        riskToken,
        riskMetrics,
        isSpotOverride,
        isFreezeSpotOverride,
        freezeSpotValue,
        isOtcTraderVols,
      };

      if (isSpotAggregatedAccounts) {
        addAggregatedData({
          ...commonProps,
          exchange,
        });
      } else {
        if (dataParent === riskTypes.spotLadder) {
          getListRiskSnapshots({ accounts });
        }

        addSpotDataToAccount({
          account: accounts,
          ...commonProps,
        });
      }

      if (!autoUpdate) setLoadingByRiskToken();
    },
    [
      addSpotDataToAccount,
      addAggregatedData,
      setIsSpotLadderQuoteLoading,
      setIsSpotLadderBaseLoading,
      setIsSpotGridQuoteLoading,
      setIsSpotGridBaseLoading,
      setIsSpotTimeQuoteLoading,
      setIsSpotTimeBaseLoading,
      setCounterAccountsAggregatedResponse,
      getListRiskSnapshots,
    ],
  );

  const addDefaultFilter = (defaultArr: ButtonType[], list: (string | number)[]) => {
    const nList = [...list];

    defaultArr.forEach((item) => {
      if (item.isDefault && !nList.some((nItem) => nItem.toString() === item.value)) {
        nList.push(`${item.value}`);
      }
    });

    return nList as string[];
  };

  const getData = useCallback(
    ({
      account: accounts,
      dataParent,
      riskTokenParam: riskToken,
      riskSettings,
      isSpotAggregatedAccounts,
      accountsAggregated,
      autoUpdate = false,
    }: IGetDataParams): Promise<void> => {
      return new Promise((resolve) => {
        const riskMetricPositions = RISK_METRIC_LIST.reduce((acc: any, metric) => {
          acc[metric.value] = metric.position;
          return acc;
        }, {});

        // A disabled metric must never reach the backend: an undefined metric
        // fails the whole risk run. Scrub once here so both the returned metric
        // list and the RISK_TYPES payload are clean - including a disabled
        // metric left over in a user's persisted risk settings.
        const requestableRiskMetrics = excludeDisabledRiskMetrics(riskSettings.riskMetrics);

        const sortedRiskMetrics = [...requestableRiskMetrics].sort(
          (a, b) => (riskMetricPositions[a] || 0) - (riskMetricPositions[b] || 0),
        );

        const volShocksParams = addDefaultFilter(VOL_SHOCK_LIST, riskSettings.volShocks).map((vs) =>
          parseFloat(vs),
        );
        const timesParams = addDefaultFilter(TIME_LIST, riskSettings.times);

        const computedRiskTypes = [
          ...FIXED_RISK_TYPES,
          ...DYNAMIC_RISK_TYPES.filter((type) => requestableRiskMetrics.includes(type)),
        ];

        const defaultConfig = {
          RISK_TYPES: computedRiskTypes,
          SPOT_SHOCK_TYPE: 'MULTIPLICATIVE',
          SPOT_SHOCKS: DEFAULT_HEADER.map((h) => h / 100),
          VOL_SHOCKS: volShocksParams,
          GROUPBY: ['Contract', 'Expiry', 'Instrument'],
          RISK_TOKEN: getStringToUpperCase(riskToken),
          HORIZON_DATES: timesParams,
          UNDERLYING: [],
        };

        const matrixConfigs: any = {
          [riskTypes.spotGrid]: {
            ...defaultConfig,
            VOL_SHOCKS: [
              -0.75, -0.5, -0.25, -0.1, -0.05, -0.04, -0.03, -0.02, -0.01, -0.005, 0, 0.005, 0.01,
              0.02, 0.03, 0.04, 0.05, 0.1, 0.25, 0.5, 0.75, 1.0, 1.25, 1.5,
            ],

            HORIZON_DATES: ['0D'],
          },
          [riskTypes.spotTime]: {
            ...defaultConfig,
            VOL_SHOCKS: [0],
            HORIZON_DATES: ['0D', '1D', '2D', '3D', '4D', '5D'],
          },
        };

        const config: any = {
          [riskTypes.spotGrid]: matrixConfigs[dataParent],
          [riskTypes.spotTime]: matrixConfigs[dataParent],
          [riskTypes.spotLadder]: defaultConfig,
        };

        const aggregatedExchanges = new Set(
          accountsAggregated?.map((accAgg) => accAgg.exchange) || [],
        );
        const hasMultipleAggregatedExchanges = aggregatedExchanges.size > 1;
        const hasAnyAggregatedExchange = aggregatedExchanges.size > 0;

        const initializeLoadingState = (
          loadingStateSetter: (value: boolean) => void,
          counterKey: keyof CounterAccountsAggregatedResponseProps,
        ) => {
          if (isSpotAggregatedAccounts && hasMultipleAggregatedExchanges) {
            loadingStateSetter(true);
          } else if (hasAnyAggregatedExchange) {
            if (counterKey) {
              setCounterAccountsAggregatedResponse((state) => ({
                ...state,
                [counterKey]: (state[counterKey] || 0) + 1,
              }));
            }
          }
          loadingStateSetter(true);
        };

        if (riskSettingsSelected.volShocks.length > 0) {
          const defaultVolShock = riskSettingsSelected.volShocks.includes(0)
            ? '0'
            : riskSettingsSelected.volShocks[0].toString();
          setVolShock(defaultVolShock);
        }

        if (riskSettingsSelected.times.length > 0) {
          setTime(riskSettingsSelected.times[0]);
        }

        if (riskSettings.riskMetrics.length > 0) {
          if (riskMetric && riskSettings.riskMetrics.includes(riskMetric)) {
            setRiskMetric(riskMetric);
          } else {
            setRiskMetric(riskSettings.riskMetrics[0]);
          }
        }

        const exchange = isSpotAggregatedAccounts ? accounts[0].exchange : accounts.exchange;

        const accountsArray = Array.isArray(accounts) ? accounts : [accounts];
        const freezeSpotData = freezeSpotMapRef.current || {};

        function transformTokenToUnderscore(token: string) {
          const lowerToken = token.toLowerCase();
          const suffixes = ['usdt', 'usdc', 'usd'];

          for (const suffix of suffixes) {
            if (lowerToken.endsWith(suffix)) {
              const base = lowerToken.slice(0, -suffix.length);
              return `${base}_${suffix}`;
            }
          }
          return lowerToken;
        }

        const transformedFreezeSpotData = Object.entries(freezeSpotData).reduce(
          (acc: Record<string, number>, [token, value]) => {
            const found = TOKENS.find((t) => t.value.includes(token));
            if (found) {
              acc[found.tokenKey] = value;
            } else {
              acc[transformTokenToUnderscore(token)] = value;
            }
            return acc;
          },
          {} as Record<string, number>,
        );

        const sources = accountsArray.map((account) => {
          if (account.exchange === 'otc') {
            return {
              account: account.name,
              venue: account.venue,
            };
          } else {
            return { name: account.name };
          }
        });

        const accountKey = Array.isArray(accounts)
          ? 'AGG'
          : `ACC_${getStringToUpperCase(accounts?.name || '')}`;

        const payloadId = `${KEY}${dataParent}_${getStringToUpperCase(exchange)}_${getStringToUpperCase(riskToken)}_${accountKey}`;

        setLoadingForRequestId(payloadId, true);

        if (!riskSettings.isFreezeSpotOverride) {
          if (freezeSpotMapRef.current) {
            freezeSpotMapRef.current = {};
          }
        }

        if (dataParent === riskTypes.spotLadder && !isSpotAggregatedAccounts) {
          getRisks(accounts, riskToken, riskSettings, true);
        }

        const payload = {
          jsonrpc: '2.0',
          id: payloadId,
          method: 'run_risk_engine',
          params: {
            config: config[dataParent],
            market_overrides: {
              spot_overrides: {
                all: riskSettings.isSpotOverride ? '00:00:00' : null,
                token_pairs: riskSettings.isFreezeSpotOverride
                  ? { ...transformedFreezeSpotData }
                  : {},
              },
              vol_surface_overrides: { all: riskSettings.isOtcTraderVols ? 'latest' : null },
            },
            sources,
          },
        };

        const handleResponseParams = {
          accounts,
          exchange,
          dataParent,
          riskToken: riskToken.toLowerCase(),
          riskMetrics: sortedRiskMetrics,
          isSpotOverride: riskSettings.isSpotOverride,
          isFreezeSpotOverride: riskSettings.isFreezeSpotOverride,
          freezeSpotValue: riskSettings.freezeSpotValue,
          isOtcTraderVols: riskSettings.isOtcTraderVols,
          isSpotAggregatedAccounts,
          autoUpdate,
        };

        if (!autoUpdate) {
          if (dataParent === riskTypes.spotLadder) {
            const counterKey =
              riskToken.toLowerCase() === 'quote' ? 'SPOT_LADDER_QUOTE' : 'SPOT_LADDER_BASE';
            initializeLoadingState(
              riskToken.toLowerCase() === 'quote'
                ? setIsSpotLadderQuoteLoading
                : setIsSpotLadderBaseLoading,
              counterKey,
            );
          } else if (dataParent === riskTypes.spotGrid) {
            const counterKey =
              riskToken.toLowerCase() === 'quote' ? 'SPOT_GRID_QUOTE' : 'SPOT_GRID_BASE';
            initializeLoadingState(
              riskToken.toLowerCase() === 'quote'
                ? setIsSpotGridQuoteLoading
                : setIsSpotGridBaseLoading,
              counterKey,
            );
          } else if (dataParent === riskTypes.spotTime) {
            const counterKey =
              riskToken.toLowerCase() === 'quote' ? 'SPOT_TIME_QUOTE' : 'SPOT_TIME_BASE';
            initializeLoadingState(
              riskToken.toLowerCase() === 'quote'
                ? setIsSpotTimeQuoteLoading
                : setIsSpotTimeBaseLoading,
              counterKey,
            );
          }
        }

        sendMessage(JSON.stringify(payload));
        subscribeToChannel(payloadId, (response: any) => {
          setLoadingForRequestId(payloadId, false);
          handleResponse(response, handleResponseParams);
          resolve(response);
        });
      });
    },
    [
      sendMessage,
      setIsSpotLadderQuoteLoading,
      setIsSpotLadderBaseLoading,
      setIsSpotGridQuoteLoading,
      setIsSpotGridBaseLoading,
      setIsSpotTimeQuoteLoading,
      setIsSpotTimeBaseLoading,
      riskSettingsSelected,
      setLoadingForRequestId,
    ],
  );

  useEffect(() => {
    const checkAndResetCounter = (
      counterKey: keyof CounterAccountsAggregatedResponseProps,
      setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      if (counterAccountsAggregatedResponse[counterKey] === 2) {
        setLoading(false);
        setCounterAccountsAggregatedResponse((state) => ({
          ...state,
          [counterKey]: 0,
        }));
      }
    };

    checkAndResetCounter('SPOT_LADDER_QUOTE', setIsSpotLadderQuoteLoading);
    checkAndResetCounter('SPOT_GRID_QUOTE', setIsSpotGridQuoteLoading);
    checkAndResetCounter('SPOT_TIME_QUOTE', setIsSpotTimeQuoteLoading);
    checkAndResetCounter('SPOT_LADDER_BASE', setIsSpotLadderBaseLoading);
    checkAndResetCounter('SPOT_GRID_BASE', setIsSpotGridBaseLoading);
    checkAndResetCounter('SPOT_TIME_BASE', setIsSpotTimeBaseLoading);
  }, [counterAccountsAggregatedResponse]);

  const riskLoadings = {
    isSpotLadderQuoteLoading,
    isSpotLadderBaseLoading,
    isSpotGridQuoteLoading,
    isSpotGridBaseLoading,
    isSpotTimeQuoteLoading,
    isSpotTimeBaseLoading,
  };

  return (
    <RiskManagementContext.Provider
      value={{
        getData,
        isRiskAllSpotLoading,
        riskLoadings,
        spotData,
        data,
        setData,
        isAutoUpdate,
        setIsAutoUpdate,
        selectedTimestampPerAccount,
        setSelectedTimestampPerAccount,
        dataGraph,
        setDataGraph,
        getLoadRiskReport,
        getListRiskSnapshots,
        listRiskSnapshotsLoading,
        riskSnapshotsData,
        setLoadingForRequestId,
        isLoadingForRequestId,
        getRequestId,
        isLoadingByParams,
        loadingByRequestId,
      }}
    >
      {children}
    </RiskManagementContext.Provider>
  );
};
